/**
 * BTM_PAYMENT_SOLUTION_SPEC_v5, Component 1 — homegrown currency converter.
 * Built against the real Dawn theme pulled from beyond-the-mat-payments-dev
 * (theme #189997744491). Reads raw IDR amounts from data-idr-price /
 * data-idr-compare-price / data-idr-price-min / data-idr-price-max /
 * data-idr-unit-price attributes added directly to snippets/price.liquid,
 * snippets/unit-price.liquid, sections/main-cart-items.liquid,
 * sections/main-cart-footer.liquid, and snippets/cart-drawer.liquid.
 *
 * Shopify convention: theme money values (product.price, cart.total_price,
 * etc.) are integers scaled by 100 even for zero-decimal currencies like
 * IDR — divide by 100 before applying an FX rate.
 *
 * Never replaces the original Shopify-rendered price text (that stays the
 * checkout currency of record). Instead appends a small "≈ $X.XX USD"
 * estimate badge next to each price, per spec: display conversion only.
 */
(function () {
  "use strict";

  // Shopify's actual Checkout page has its own script context — this
  // module is for storefront pages only.
  if (window.Shopify && window.Shopify.Checkout) {
    return;
  }

  var SCHEMA_VERSION = 1;
  var TTL_MS = 4 * 60 * 60 * 1000; // 4 hours, per spec
  var LOADING_TIMEOUT_MS = 8000; // spec: spinner up to 8s, then fallback state
  var MUTATION_DEBOUNCE_MS = 50;

  // Trimmed to USD/EUR only, 2026-07-14 — Brent: "the currencies in the
  // floating pill should just be euro, USD, and IDR at the moment." IDR is
  // the native/base currency (never fetched as a rate), so only USD/EUR
  // need real FX rates. AUD/GBP/CAD were removed from the picker in
  // btm-currency-selector.liquid — trimming the fetch/sanity-band config
  // to match keeps this from silently pulling rates for currencies nothing
  // offers anymore.
  var PRIMARY_RATE_URL = "https://api.frankfurter.app/latest?from=IDR&to=USD,EUR";
  var FALLBACK_RATE_URL = "https://open.er-api.com/v6/latest/IDR";

  var RATE_BANDS = {
    USD: [0.000045, 0.00009],
    EUR: [0.00004, 0.000085],
  };

  var SUPPORTED_CURRENCIES = Object.keys(RATE_BANDS);

  // --- shared payment-methods-per-currency data (2026-07-14) --------------
  // Single in-theme source for both snippets/btm-footer-currency-panel.liquid
  // and snippets/btm-currency-selector.liquid's confirmation toast — they
  // used to each hold their own copy, which is exactly the kind of drift
  // risk Brent flagged. Exposed on window rather than kept as a local var
  // here because this script is deferred (runs after DOM parse, but before
  // either consumer's inline script actually NEEDS this data — both only
  // read it inside their render functions, called later from the
  // btm:fx_ready/btm:currency_changed events this same script dispatches,
  // so by the time it's read it's always already assigned).
  //
  // CANONICAL SOURCE, cross-referenced 2026-07-14: independently mirrored
  // in beyond-the-mat-affiliates-app/extensions/btm-payment-rails/src/
  // PaymentRails.tsx as PAYPAL_DISPLAY_CURRENCIES / WISE_DISPLAY_CURRENCIES
  // (a checkout UI extension is a separate deployment with no shared
  // runtime with a theme asset — see that file's comment). If you change
  // this, check that file too.
  //
  // 2026-08-06 (Chat A): Venmo removed from USD per Brent — "we aren't
  // supporting this anymore." THEME SIDE ONLY. This does not touch
  // PaymentRails.tsx in the affiliates-app repo, which independently
  // mirrors this same list per the comment above — that file still needs
  // a matching edit or the storefront and the actual checkout extension
  // will disagree about whether Venmo is offered. Routed to Claude Code,
  // not fixed here.
  //
  // Card/Xendit checkout works at any displayed currency (always settles
  // in IDR) — Brent, verbatim, 2026-07-14: "I want them to be able to
  // select credit card at any currency no matter what. But we need
  // somehow to highlight that the payment will be an IDR."
  var PAYMENT_ICONS = {
    // Official brand marks (Simple Icons project, CC0 — simpleicons.org).
    paypal:
      '<svg role="img" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="currentColor" width="14" height="14" aria-hidden="true"><path d="M15.607 4.653H8.941L6.645 19.251H1.82L4.862 0h7.995c3.754 0 6.375 2.294 6.473 5.513-.648-.478-2.105-.86-3.722-.86m6.57 5.546c0 3.41-3.01 6.853-6.958 6.853h-2.493L11.595 24H6.74l1.845-11.538h3.592c4.208 0 7.346-3.634 7.153-6.949a5.24 5.24 0 0 1 2.848 4.686M9.653 5.546h6.408c.907 0 1.942.222 2.363.541-.195 2.741-2.655 5.483-6.441 5.483H8.714Z"/></svg>',
    wise:
      '<svg role="img" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="currentColor" width="14" height="14" aria-hidden="true"><path d="M6.488 7.469 0 15.05h11.585l1.301-3.576H7.922l3.033-3.507.01-.092L8.993 4.48h8.873l-6.878 18.925h4.706L24 .595H2.543l3.945 6.874Z"/></svg>',
  };

  var PAYMENT_METHODS_BY_CURRENCY = {
    USD: [
      { icon: "💳", label: "Card", note: "charged in IDR" },
      { svg: PAYMENT_ICONS.paypal, label: "PayPal" },
      { svg: PAYMENT_ICONS.wise, label: "Wise" },
    ],
    EUR: [
      { icon: "💳", label: "Card", note: "charged in IDR" },
      { svg: PAYMENT_ICONS.paypal, label: "PayPal" },
      { svg: PAYMENT_ICONS.wise, label: "Wise" },
    ],
    IDR: [
      { icon: "💳", label: "Card" },
      { svg: PAYMENT_ICONS.wise, label: "Wise" },
    ],
  };

  window.BTM_PAYMENT_METHODS = PAYMENT_METHODS_BY_CURRENCY;

  var PRICE_SELECTOR = "[data-idr-price]";
  var COMPARE_PRICE_SELECTOR = "[data-idr-compare-price]";
  var UNIT_PRICE_SELECTOR = "[data-idr-unit-price]";
  var RANGE_SELECTOR = "[data-idr-price-min]";
  var ALL_PRICE_SELECTORS = [PRICE_SELECTOR, COMPARE_PRICE_SELECTOR, UNIT_PRICE_SELECTOR, RANGE_SELECTOR];
  var BADGE_CLASS = "btm-fx-badge";

  function isRateSane(currency, rate) {
    var band = RATE_BANDS[currency];
    return !!band && typeof rate === "number" && rate >= band[0] && rate <= band[1];
  }

  // --- localStorage contract (spec: LOCALSTORAGE SCHEMA) ---------------

  function readCache() {
    try {
      var raw = window.localStorage.getItem("btm_fx_cache");
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (parsed.btm_fx_schema_version !== SCHEMA_VERSION) return null;
      return parsed;
    } catch (e) {
      return null;
    }
  }

  function writeCache(partial) {
    try {
      var current = readCache() || {};
      var next = Object.assign(
        {
          btm_fx_schema_version: SCHEMA_VERSION,
          btm_currency_selected: null,
          // True only when the customer explicitly picked a currency (pill
          // or footer panel click) — see setCurrency() below. Region-driven
          // defaults (resolveSelectedCurrency) never set this, so an
          // explicit pick always outranks a later region change; an
          // auto-picked default keeps following region changes indefinitely.
          btm_currency_manual_override: false,
          btm_fx_rates: {},
          btm_fx_fetched_at: null,
          btm_fx_ready: false,
        },
        current,
        partial
      );
      window.localStorage.setItem("btm_fx_cache", JSON.stringify(next));
      return next;
    } catch (e) {
      // localStorage unavailable — converter still works for this page
      // load, just re-fetches every time. Not fatal.
      return null;
    }
  }

  function isCacheFresh(cache) {
    if (!cache || !cache.btm_fx_fetched_at) return false;
    return Date.now() - cache.btm_fx_fetched_at < TTL_MS;
  }

  // --- rate fetching (spec: primary + fallback, sanity bands) ----------

  function parseFrankfurterResponse(json) {
    // Shape: { amount, base: "IDR", date, rates: { USD: ..., EUR: ... } }
    return json && json.rates ? json.rates : null;
  }

  function parseOpenErApiResponse(json) {
    // Shape: { result: "success", base_code: "IDR", rates: { USD: ..., ... } }
    return json && json.rates ? json.rates : null;
  }

  function fetchFrom(url, parse) {
    return fetch(url)
      .then(function (res) {
        if (!res.ok) throw new Error("HTTP " + res.status);
        return res.json();
      })
      .then(function (json) {
        var rawRates = parse(json);
        if (!rawRates) throw new Error("no rates in response");
        var sane = {};
        for (var i = 0; i < SUPPORTED_CURRENCIES.length; i++) {
          var c = SUPPORTED_CURRENCIES[i];
          var r = rawRates[c];
          if (isRateSane(c, r)) sane[c] = r;
        }
        if (Object.keys(sane).length === 0) throw new Error("no sane rates");
        return sane;
      });
  }

  function fetchFreshRates() {
    return fetchFrom(PRIMARY_RATE_URL, parseFrankfurterResponse).catch(function () {
      dispatchBtmEvent("rate_fetch_failed", { source: "primary" });
      return fetchFrom(FALLBACK_RATE_URL, parseOpenErApiResponse);
    });
  }

  // --- currency selection (spec: dev mode override, locale-based default) ---

  function getDevModeOverride() {
    try {
      var params = new URLSearchParams(window.location.search);
      var currency = params.get("btm_currency");
      var debug = params.get("btm_debug") === "true";
      if (currency && (SUPPORTED_CURRENCIES.indexOf(currency) !== -1 || currency === "IDR")) {
        return { currency: currency, debug: debug };
      }
    } catch (e) {
      // URLSearchParams unsupported or malformed query — ignore, no override.
    }
    return null;
  }

  function guessCurrencyFromLocale() {
    // Only ever returns a currency actually in RATE_BANDS — GB/AU/CA
    // locale guesses were removed along with the AUD/GBP/CAD pill options
    // (2026-07-14); those visitors now fall through to USD like any other
    // non-EU locale, rather than guessing a currency with no rate to apply.
    try {
      var locale = (navigator.language || "en-US").toUpperCase();
      if (/DE|FR|ES|IT|NL|BE|PT|AT|IE|FI/.test(locale)) return "EUR";
      return "USD";
    } catch (e) {
      return "USD";
    }
  }

  // --- region-driven currency default (2026-07-15) -----------------------
  // ISO 3166-1 alpha-2 codes for Eurozone countries (the ones that actually
  // use EUR — not all EU member states; e.g. Sweden is EU but SEK). Kept as
  // a plain list rather than a locale regex like guessCurrencyFromLocale
  // above, because this reads a real country selection (window.BTM_COUNTRY,
  // sourced from Shopify's own localization.country.iso_code — see
  // layout/theme.liquid), not a guess from browser language.
  var EUR_COUNTRY_CODES = [
    "AT", "BE", "CY", "DE", "EE", "ES", "FI", "FR", "GR", "HR", "IE", "IT",
    "LT", "LU", "LV", "MT", "NL", "PT", "SI", "SK",
    "AD", "MC", "SM", "VA", // micro-states also on EUR
  ];

  // Maps the native footer country selector's current country to a
  // default display currency: Indonesia -> IDR (native/base currency),
  // Eurozone -> EUR, everything else -> USD (the universal fallback — every
  // rail PayPal/Wise offers accepts USD). Returns null only if
  // window.BTM_COUNTRY itself wasn't set (theme.liquid should always set
  // it, but this script has no hard dependency on that).
  function resolveRegionCurrency() {
    if (!window.BTM_COUNTRY || typeof window.BTM_COUNTRY !== "string") return null;
    var code = window.BTM_COUNTRY.toUpperCase();
    if (code === "ID") return "IDR";
    if (EUR_COUNTRY_CODES.indexOf(code) !== -1) return "EUR";
    return "USD";
  }

  // Single priority-ordered source of truth for "what currency should be
  // selected right now" (excluding the URL dev-override, handled
  // separately by callers so its own non-persistence rule stays intact):
  //   1. an explicit customer pick (btm_currency_manual_override) — always
  //      wins, region changes never override it.
  //   2. the native region selector's current country, mapped to a
  //      currency — so region changes keep updating the default for
  //      anyone who hasn't explicitly chosen one.
  //   3. a previous auto-picked default, if region is unresolvable this load.
  //   4. browser-locale guess, last resort.
  function resolveSelectedCurrency(cache) {
    if (cache && cache.btm_currency_manual_override && cache.btm_currency_selected) {
      return cache.btm_currency_selected;
    }
    var regionCurrency = resolveRegionCurrency();
    if (regionCurrency) return regionCurrency;
    if (cache && cache.btm_currency_selected) return cache.btm_currency_selected;
    return guessCurrencyFromLocale();
  }

  // --- DOM conversion application ---------------------------------------

  function formatMoneyOnly(amount, currency) {
    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: currency,
        currencyDisplay: "narrowSymbol",
      }).format(amount);
    } catch (e) {
      return amount.toFixed(2);
    }
  }

  // USD/AUD/CAD all render with a bare "$" in most locales — always append
  // the ISO code too so a converted price is never ambiguous.
  function formatEstimate(amount, currency) {
    return "≈ " + formatMoneyOnly(amount, currency) + " " + currency;
  }

  function ensureBadge(el) {
    var badge = el.querySelector("." + BADGE_CLASS);
    if (!badge) {
      badge = document.createElement("span");
      badge.className = BADGE_CLASS;
      badge.setAttribute("data-btm-badge", "");
      badge.setAttribute("aria-hidden", "false");
      el.appendChild(badge);
    }
    return badge;
  }

  function removeAllBadges(root) {
    var badges = root.querySelectorAll("." + BADGE_CLASS);
    for (var i = 0; i < badges.length; i++) {
      badges[i].remove();
    }
    return badges.length;
  }

  function applyToSingleValueSelector(root, selector, attr, currency, rate) {
    var els = root.querySelectorAll(selector);
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      var rawCents = parseFloat(el.getAttribute(attr));
      if (isNaN(rawCents)) continue;
      var amount = (rawCents / 100) * rate;
      var badge = ensureBadge(el);
      badge.textContent = formatEstimate(amount, currency);
      dispatchBtmEvent("price_converted", { element: el, currency: currency, amount: amount });
    }
  }

  function applyToRangeSelector(root, currency, rate) {
    var els = root.querySelectorAll(RANGE_SELECTOR);
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      var rawMin = parseFloat(el.getAttribute("data-idr-price-min"));
      var rawMax = parseFloat(el.getAttribute("data-idr-price-max"));
      if (isNaN(rawMin) || isNaN(rawMax)) continue;
      var amountMin = (rawMin / 100) * rate;
      var amountMax = (rawMax / 100) * rate;
      var badge = ensureBadge(el);
      badge.textContent =
        "≈ " +
        formatMoneyOnly(amountMin, currency) +
        "–" +
        formatMoneyOnly(amountMax, currency) +
        " " +
        currency;
      dispatchBtmEvent("price_converted", { element: el, currency: currency, amountMin: amountMin, amountMax: amountMax });
    }
  }

  function applyConversion(root, currency, rates) {
    if (currency === "IDR") {
      var removed = removeAllBadges(root);
      dispatchBtmEvent("currency_changed", { currency: "IDR", badgesRemoved: removed });
      return;
    }
    var rate = rates[currency];
    if (typeof rate !== "number") return;
    applyToSingleValueSelector(root, PRICE_SELECTOR, "data-idr-price", currency, rate);
    applyToSingleValueSelector(root, COMPARE_PRICE_SELECTOR, "data-idr-compare-price", currency, rate);
    applyToSingleValueSelector(root, UNIT_PRICE_SELECTOR, "data-idr-unit-price", currency, rate);
    applyToRangeSelector(root, currency, rate);
  }

  // --- dynamic content watcher --------------------------------------------
  // Watches for data-idr-* elements added after initial page load — variant
  // changes, cart drawer/page updates, filter re-renders, quick-add modals,
  // recommendations hydration. All of these replace whole subtrees fetched
  // fresh from the server (confirmed against Dawn's cart.js/product-info.js/
  // facets.js), so any data-idr-* attribute baked into the Liquid survives
  // automatically — this observer just needs to notice new nodes and re-run
  // the (idempotent) conversion pass over them.

  function nodeHasPriceAttribute(node) {
    for (var i = 0; i < ALL_PRICE_SELECTORS.length; i++) {
      var attr = ALL_PRICE_SELECTORS[i].slice(1, -1); // "[data-idr-price]" -> "data-idr-price"
      if (node.hasAttribute && node.hasAttribute(attr)) return true;
    }
    for (var j = 0; j < ALL_PRICE_SELECTORS.length; j++) {
      if (node.querySelector && node.querySelector(ALL_PRICE_SELECTORS[j])) return true;
    }
    return false;
  }

  function setupMutationObserver(getState) {
    var pending = false;
    var observer = new MutationObserver(function (mutations) {
      if (pending) return;
      var hasRelevantAddition = mutations.some(function (m) {
        if (!m.addedNodes || m.addedNodes.length === 0) return false;
        for (var i = 0; i < m.addedNodes.length; i++) {
          var node = m.addedNodes[i];
          if (node.nodeType !== 1) continue; // element nodes only
          if (nodeHasPriceAttribute(node)) return true;
        }
        return false;
      });
      if (!hasRelevantAddition) return;
      pending = true;
      setTimeout(function () {
        pending = false;
        var state = getState();
        if (state.ready && state.currency && state.rates) {
          applyConversion(document.body, state.currency, state.rates);
        }
      }, MUTATION_DEBOUNCE_MS);
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return observer;
  }

  // --- instrumentation (spec: currency_selected, currency_changed, --------
  // converter_hidden, rate_fetch_failed, price_converted) ------------------

  function dispatchBtmEvent(name, detail) {
    try {
      window.dispatchEvent(new CustomEvent("btm:" + name, { detail: detail }));
    } catch (e) {
      // CustomEvent unsupported — extremely old browser, not worth a
      // fallback path for this instrumentation-only concern.
    }
  }

  // --- badge styling -------------------------------------------------------

  // Every price target becomes a small vertical stack: converted estimate
  // on top (larger, bolder), original IDR price below (smaller, muted).
  // column-reverse lets the badge visually lead while staying AFTER the
  // original price in DOM order — screen readers still hit the IDR price,
  // the authoritative value, first.
  var PRICE_TARGET_SELECTOR = ALL_PRICE_SELECTORS.map(function (s) {
    return s + ":has(." + BADGE_CLASS + ")";
  }).join(",");

  function injectBadgeStyles() {
    if (document.getElementById("btm-fx-badge-style")) return;
    var style = document.createElement("style");
    style.id = "btm-fx-badge-style";
    style.textContent =
      PRICE_TARGET_SELECTOR + "{" +
      "display:inline-flex;flex-direction:column-reverse;align-items:flex-start;" +
      "gap:0.1em;line-height:1.2;}" +
      "." + BADGE_CLASS + "{display:block;font-size:1.2em;font-weight:700;" +
      "color:inherit;opacity:0;animation:btm-fx-badge-in 260ms ease forwards;}" +
      "@keyframes btm-fx-badge-in{from{opacity:0;transform:translateY(2px);}" +
      "to{opacity:1;transform:translateY(0);}}";
    document.head.appendChild(style);
  }

  // --- main init ---------------------------------------------------------

  function init() {
    injectBadgeStyles();

    var state = {
      ready: false,
      currency: null,
      rates: {},
    };

    function setReady(currency, rates) {
      state.ready = true;
      state.currency = currency;
      state.rates = rates;
      dispatchBtmEvent("fx_ready", { currency: currency, rates: rates });
      applyConversion(document.body, currency, rates);
    }

    // Set these up unconditionally, before any early-return below. Found
    // via live testing 2026-07-14: these were previously placed after the
    // cache-fast-path's `return`, which meant window.BTMCurrencyConverter
    // was never defined on any normal page load after the first (once a
    // fresh cache existed, the fast path returned before reaching here) —
    // the currency picker silently no-op'd on every subsequent page. A
    // dev-override query param bypassed that same early return, which is
    // why it "worked" there and nowhere else.
    setupMutationObserver(function () {
      return state;
    });

    window.BTMCurrencyConverter = {
      setCurrency: function (currency) {
        if (SUPPORTED_CURRENCIES.indexOf(currency) === -1 && currency !== "IDR") return;
        state.currency = currency;
        // Every caller of this method is a real user click (pill or footer
        // panel) — see resolveSelectedCurrency's priority order above for
        // why that needs to be recorded, not just the currency itself.
        writeCache({ btm_currency_selected: currency, btm_currency_manual_override: true });
        applyConversion(document.body, currency, state.rates);
        if (currency !== "IDR") dispatchBtmEvent("currency_changed", { currency: currency });
      },
      getState: function () {
        return state;
      },
    };

    var devOverride = getDevModeOverride();
    var cache = readCache();

    // Race condition guard (spec F02): if a fetch is in flight and a valid
    // cache exists, use the cache immediately rather than waiting.
    if (cache && isCacheFresh(cache) && cache.btm_currency_selected) {
      var fastCurrency = devOverride ? devOverride.currency : resolveSelectedCurrency(cache);
      // Region changed since the last write (and no manual override) —
      // persist the new default so it's what's cached on the next load too.
      if (!devOverride && fastCurrency !== cache.btm_currency_selected) {
        writeCache({ btm_currency_selected: fastCurrency, btm_currency_manual_override: false });
      }
      setReady(fastCurrency, cache.btm_fx_rates);
      if (!devOverride) return; // cache fresh and no dev override — nothing else to do
    }

    writeCache({ btm_fx_ready: false });

    var loadingTimer = setTimeout(function () {
      if (!state.ready) {
        dispatchBtmEvent("converter_hidden", { reason: "loading_timeout" });
      }
    }, LOADING_TIMEOUT_MS);

    fetchFreshRates()
      .then(function (rates) {
        clearTimeout(loadingTimer);
        // Re-read localStorage here: a currency picked while this fetch was
        // in flight was persisted by setCurrency() but is absent from the
        // `cache` closure captured at init(); resolving against that stale
        // snapshot would revert the pick and mis-persist it (manual_override
        // stays true while selected reverts to the region default).
        var freshCache = readCache();
        var selected = devOverride ? devOverride.currency : resolveSelectedCurrency(freshCache);

        writeCache({
          btm_fx_rates: rates,
          btm_fx_fetched_at: Date.now(),
          btm_fx_ready: true,
          // Dev mode override never persists the override as the stored
          // preference (spec: "localStorage NOT overwritten by URL param").
          btm_currency_selected: devOverride ? (freshCache && freshCache.btm_currency_selected) || selected : selected,
        });

        setReady(selected, rates);
        dispatchBtmEvent("currency_selected", { currency: selected });
      })
      .catch(function () {
        clearTimeout(loadingTimer);
        dispatchBtmEvent("rate_fetch_failed", { source: "fallback" });
        // Both sources failed. Use stale cache if available; otherwise never
        // show a wrong amount — hide the converter, prices stay IDR-only.
        // Fresh read (not the init() closure) so an in-flight pick is honored
        // and it degrades gracefully if localStorage was cleared mid-flight.
        var freshCache = readCache();
        if (freshCache && freshCache.btm_fx_rates && Object.keys(freshCache.btm_fx_rates).length > 0) {
          setReady(resolveSelectedCurrency(freshCache), freshCache.btm_fx_rates);
        } else {
          dispatchBtmEvent("converter_hidden", { reason: "no_rates_available" });
        }
      });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
